/* eslint-disable @tanstack/query/exhaustive-deps -- doesn't work with the key factory*/
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { getErrorStatus, isTransientRequestError } from "~/api/error";
import { useAppContext } from "~/providers/app";
import { authActions, getAuth, useAuth } from "~/providers/store/auth";
import { logger } from "~/services/logger";

import { tillsKeys } from "../../keys";
import { getTill } from "../../requests";

function useDeviceId() {
  const { device } = useAppContext();

  return useMemo(() => {
    return device.deviceId;
  }, [device.deviceId]);
}

export class TillDeviceMismatchError extends Error {
  constructor(
    readonly tillId: string,
    readonly tillDeviceId: string | null,
    readonly deviceId: string
  ) {
    super(
      `Till ${tillId} is assigned to a different device than the current one - logging out till`
    );
    this.name = "TillDeviceMismatchError";
  }
}

const MAX_TRANSIENT_RETRIES = 5;

const currentTillQuery = (tillId: string | null, deviceId: string | null) =>
  queryOptions({
    queryKey: tillsKeys.detail(tillId ?? "unknown"),
    queryFn: async () => {
      if (!tillId) {
        throw new Error("User not authenticated");
      }

      const till = await getTill(tillId);

      const { isKanplaAdmin } = getAuth();
      if (isKanplaAdmin || till.deviceId === deviceId) {
        return till;
      }

      // TO-DO: CLEANUP -- Investigating a bug where deviceId is not a string
      if (typeof deviceId !== "string") {
        logger.warn("Device ID is not a string", {
          till_id: tillId,
          till_device_id: till.deviceId,
          device_id: deviceId
        });
        return till;
      }

      const error = new TillDeviceMismatchError(
        tillId,
        till.deviceId,
        deviceId
      );
      logger.warn(error.message, {
        till_id: tillId,
        till_device_id: till.deviceId,
        device_id: deviceId
      });

      authActions.logoutTill();

      throw error;
    },
    enabled: Boolean(tillId),
    refetchInterval: 1000 * 60 * 5, // 5 minutes
    // Bounded and wider than the global default: an unbounded retry keeps the
    // query in `pending` for a whole outage, so the layout cannot tell "still
    // trying" from "failed" and falls through to the licence screen.
    retry: (failureCount, error) => {
      const status = getErrorStatus(error);
      if (status === 401) {
        return false;
      }

      return (
        (status === 504 || isTransientRequestError(error)) &&
        failureCount < MAX_TRANSIENT_RETRIES
      );
    }
  });

export type TillQueryData = Awaited<
  ReturnType<NonNullable<ReturnType<typeof currentTillQuery>["queryFn"]>>
>;

export function useCurrentTill<T = TillQueryData>(
  select?: (data: TillQueryData) => T
) {
  const { tillId } = useAuth();
  const deviceId = useDeviceId();

  return useQuery({
    ...currentTillQuery(tillId, deviceId),
    select
  });
}
